// Города на карте «Evə yaxın» (/tutors) — справочник map_cities (20261028120000).
//
// Решение владельца 16.10.2026: справочник не в коде, а в базе. Здесь — только
// чистые помощники над его строками; сами строки приходят с сервера
// (map-cities.server.ts → loadMapCities). Их зовут форма join, кабинет,
// сервер анкеты, карта и scripts/verify-map-cities.mts.
//
// ГОРОД ПО ПИНУ — ОДНА ФУНКЦИЯ ДЛЯ КЛИЕНТА И СЕРВЕРА (cityByPin). Внутри
// простого контура Баку — 'baku' и бакинский район, как раньше. Вне контура —
// ближайший центр из map_cities, без района и метро. Google для города больше
// не спрашиваем: анкета из Кюрдамира сохранялась бакинской (ayten-memmedova).
import { BAKU_CITY, type Lang3 } from "@/data/map-taxonomy";

// Код Баку — один, в map-taxonomy (там же районы и метро Баку).
export { BAKU_CITY };
/** Значение фильтра «Bütün ölkə / Вся страна». В map_cities такого кода нет. */
export const ALL_CITIES = "all";

/** Строка map_cities. lat/lng — центр города или райцентра. */
export type MapCity = {
  code: string;
  name_az: string;
  name_ru: string;
  name_en: string;
  kind: "city" | "district";
  region: "ar" | "nakhchivan";
  lat: number;
  lng: number;
  sort_order: number;
  is_active: boolean;
};

/** Подписи города на трёх языках — едут вместе с анкетой (PublicPartner.city_name). */
export type CityName = { az: string; ru: string; en: string };

export const cityNameOf = (c: MapCity): CityName => ({ az: c.name_az, ru: c.name_ru, en: c.name_en });

/** Подписи города по коду; нет кода или его нет в справочнике — null. */
export function cityNameFor(cities: readonly MapCity[], code: string | null | undefined): CityName | null {
  const c = code ? cities.find((x) => x.code === code) : undefined;
  return c ? cityNameOf(c) : null;
}

/** Подпись города по коду; нет в справочнике — сам код (так же, как labelOf). */
export function cityLabel(cities: readonly MapCity[], code: string, lang: Lang3): string {
  const c = cities.find((x) => x.code === code);
  return c ? cityNameOf(c)[lang] : code;
}

export const isActiveCity = (cities: readonly MapCity[], code: string) =>
  cities.some((c) => c.code === code && c.is_active);

/**
 * ПРОСТОЙ КОНТУР БАКУ (широта, долгота), по часовой стрелке. Грубый, без
 * внешних API: побережье Апшерона с Пираллахи и Шахдили, на юге Гарадаг до
 * Алята, на западе граница с Абшеронским районом.
 *
 * Запад нарочно взят с запасом. Хырдалан (Абшерон) и бакинские посёлки
 * Бинагадинского района (Сулутепе, Биляджари) перемешаны, простым контуром их
 * не развести. Ошибка «хырдаланский репетитор — бакинский» безобиднее
 * обратной: бакинский останется со своим районом и метро. Масазыр, Новханы,
 * Сарай, Джейранбатан и Сумгаит — снаружи.
 *
 * Тот же контур — в бэкфилле 20261029120000 (polygon в SQL). Сверяет их
 * scripts/verify-map-cities.mts.
 */
export const BAKU_CONTOUR: ReadonlyArray<readonly [number, number]> = [
  [40.6, 49.86],
  [40.6, 49.93],
  [40.575, 50.06],
  [40.53, 50.23],
  [40.49, 50.4],
  [40.37, 50.4],
  [40.32, 50.08],
  [40.29, 49.92],
  [40.24, 49.7],
  [40.1, 49.48],
  [39.9, 49.44],
  [39.94, 49.25],
  [40.2, 49.3],
  [40.33, 49.56],
  [40.4, 49.68],
  [40.475, 49.7],
  [40.475, 49.8],
  [40.52, 49.83],
];

/** Пин внутри контура Баку — луч по долготе (ray casting). */
export function inBakuContour(lat: number, lng: number): boolean {
  let inside = false;
  const n = BAKU_CONTOUR.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [yi, xi] = BAKU_CONTOUR[i];
    const [yj, xj] = BAKU_CONTOUR[j];
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * Город по пину. Внутри контура — Баку. Снаружи — ближайший центр среди
 * действующих строк map_cities, кроме Баку (пин за контуром — не Баку, даже
 * если до центра Баку ближе, чем до Хырдалана). Плоская формула с поправкой
 * долготы на широту — как у nearestMetro; для страны размером с Азербайджан
 * её хватает. Справочник пуст — null.
 */
export function cityByPin(lat: number, lng: number, cities: readonly MapCity[]): string | null {
  if (inBakuContour(lat, lng)) return BAKU_CITY;
  const k = Math.cos((lat * Math.PI) / 180);
  let best: string | null = null;
  let bestD = Infinity;
  for (const c of cities) {
    if (!c.is_active || c.code === BAKU_CITY) continue;
    const d = (c.lat - lat) ** 2 + ((c.lng - lng) * k) ** 2;
    if (d < bestD) {
      bestD = d;
      best = c.code;
    }
  }
  return best;
}

/** Порядок справочника: Баку первым, дальше sort_order. */
export const sortCities = (cities: readonly MapCity[]): MapCity[] =>
  [...cities].sort((a, b) => (a.code === BAKU_CITY ? -1 : b.code === BAKU_CITY ? 1 : a.sort_order - b.sort_order));

type WithCity = { address_mode: "building" | "travel" | "online"; city?: string | null };

/** Город анкеты; онлайн — без города (null). Анкеты без city (до 20261025120000) — бакинские. */
export function cityOfPartner(p: WithCity): string | null {
  if (p.address_mode === "online") return null;
  return p.city || BAKU_CITY;
}

/**
 * Города фильтра карты (решение владельца 16.10.2026): Баку всегда, остальные —
 * если в городе есть хоть одна опубликованная анкета (не онлайн). Порядок —
 * справочника. Порога в три анкеты больше нет.
 */
export function filterCities(rows: WithCity[], cities: readonly MapCity[]): string[] {
  const have = new Set(rows.map(cityOfPartner).filter((c): c is string => !!c));
  return sortCities(cities)
    .map((c) => c.code)
    .filter((c) => c === BAKU_CITY || have.has(c));
}

/**
 * Вкладка «Рядом»: анкеты выбранного города, при «Bütün ölkə» — всех городов.
 * Вкладка «Онлайн» от города не зависит: только онлайн-анкеты.
 */
export function inTab(p: WithCity, tab: "near" | "online", city: string): boolean {
  const c = cityOfPartner(p);
  if (tab === "online") return c === null;
  return c !== null && (city === ALL_CITIES || c === city);
}
